"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import NotificationTestButton from "../../components/NotificationTestButton"
import { Mail, MessageSquare, Check, X, Clock, AlertCircle, CheckCircle2, XCircle, RefreshCw } from "lucide-react"
import { notificationTemplatesAPI, notificationSettingsAPI, reminderScheduleAPI, smsProviderAPI } from "../../services/api"
import { useAuth } from "../../context/AuthContext"
import SettingsRailLayout from "../../components/settings-rail-layout"
import SfSwitch from "../../components/ui/sf-switch"

// Provider id -> display label, per issue #75 phase 2 follow-up on
// goscha's "option to choose notification channel from the connected
// services". Matches the enum in lib/automated-sms-gate.js backend.
const PROVIDER_LABELS = {
  twilio: "Twilio",
  openphone: "OpenPhone",
  callio: "Callio",
  ringcentral: "RingCentral",
  leadbridge: "LeadBridge",
}

// Lead-time choices shown to the user. Hour value -> label.
// goscha's issue #76 explicitly calls out 3 days / 1 day / 1 hour.
const LEAD_TIME_CHOICES = [
  { hours: 168, label: "1 week before" },
  { hours: 72, label: "3 days before" },
  { hours: 48, label: "2 days before" },
  { hours: 24, label: "1 day before" },
  { hours: 4, label: "4 hours before" },
  { hours: 2, label: "2 hours before" },
  { hours: 1, label: "1 hour before" },
]

const AppointmentReminder = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [enableEmail, setEnableEmail] = useState(true)
  const [enableSMS, setEnableSMS] = useState(true)
  const [showLogo, setShowLogo] = useState(false)
  const [activeTab, setActiveTab] = useState("email")
  // Preview is heavy visually, so we keep it collapsed by default and
  // let the owner expand it on demand. Replaces the old 2-col layout
  // that cramped both the settings and the preview.
  const [showPreview, setShowPreview] = useState(false)
  const [emailTemplate, setEmailTemplate] = useState({
    subject: '',
    content: ''
  })
  const [smsTemplate, setSmsTemplate] = useState({
    content: ''
  })
  // Appointment reminder cron schedule — tenant flag + lead times.
  const [cronEnabled, setCronEnabled] = useState(false)
  const [leadTimesHours, setLeadTimesHours] = useState([72, 24, 1])
  const [scheduleLoading, setScheduleLoading] = useState(true)
  const [scheduleSaving, setScheduleSaving] = useState(false)
  const [scheduleError, setScheduleError] = useState('')
  const [dispatches, setDispatches] = useState([])
  const [dispatchesLoading, setDispatchesLoading] = useState(true)
  const [dispatchesError, setDispatchesError] = useState('')
  // Notification channel (SMS provider) — issue #75 phase 2.
  const [smsProviderData, setSmsProviderData] = useState(null) // { selected, effective, providers }
  const [smsProviderSaving, setSmsProviderSaving] = useState(false)
  const [smsProviderError, setSmsProviderError] = useState('')
  const navigate = useNavigate()
  const { user } = useAuth()

  const loadSchedule = useCallback(async () => {
    try {
      setScheduleLoading(true)
      setScheduleError('')
      const data = await reminderScheduleAPI.getSchedule()
      setCronEnabled(Boolean(data?.enabled))
      setLeadTimesHours(Array.isArray(data?.lead_times_hours) && data.lead_times_hours.length > 0
        ? data.lead_times_hours.slice().sort((a, b) => b - a)
        : [72, 24, 1])
    } catch (e) {
      const code = e?.response?.data?.error
      if (code === 'migration_missing') {
        setScheduleError('Database migration 085 not applied yet. Reminder schedule unavailable until then.')
      } else {
        setScheduleError('Could not load reminder schedule.')
      }
    } finally {
      setScheduleLoading(false)
    }
  }, [])

  const loadDispatches = useCallback(async () => {
    try {
      setDispatchesLoading(true)
      setDispatchesError('')
      const rows = await reminderScheduleAPI.getDispatches(20)
      setDispatches(rows)
    } catch (e) {
      const code = e?.response?.data?.error
      if (code !== 'migration_missing') {
        setDispatchesError('Could not load recent reminder activity.')
      }
    } finally {
      setDispatchesLoading(false)
    }
  }, [])

  const loadSmsProvider = useCallback(async () => {
    try {
      setSmsProviderError('')
      const data = await smsProviderAPI.getProvider()
      setSmsProviderData(data)
    } catch (e) {
      const code = e?.response?.data?.error
      setSmsProviderError(
        code === 'migration_missing'
          ? 'Run migration 086 to enable the notification channel selector.'
          : 'Could not load notification channel.'
      )
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    loadSchedule()
    loadDispatches()
    loadSmsProvider()
  }, [user?.id, loadSchedule, loadDispatches, loadSmsProvider])

  const handleChangeProvider = async (nextValue) => {
    // nextValue is '' when user picks "Auto".
    const provider = nextValue === '' ? null : nextValue
    setSmsProviderSaving(true)
    setSmsProviderError('')
    try {
      await smsProviderAPI.updateProvider(provider)
      await loadSmsProvider()
      setMessage({ type: 'success', text: 'Notification channel updated.' })
      setTimeout(() => setMessage({ type: '', text: '' }), 2500)
    } catch (e) {
      const code = e?.response?.data?.error
      setSmsProviderError(
        code === 'migration_missing'
          ? 'Run migration 086 to enable the notification channel selector.'
          : (e?.response?.data?.message || e.message || 'Could not save notification channel.')
      )
    } finally {
      setSmsProviderSaving(false)
    }
  }

  const toggleLeadTime = (hours) => {
    setLeadTimesHours((prev) => {
      const has = prev.includes(hours)
      const next = has ? prev.filter((h) => h !== hours) : [...prev, hours]
      return next.sort((a, b) => b - a)
    })
  }

  const handleSaveSchedule = async () => {
    if (leadTimesHours.length === 0) {
      setScheduleError('Pick at least one reminder time, or turn automated reminders off.')
      return
    }
    try {
      setScheduleSaving(true)
      setScheduleError('')
      await reminderScheduleAPI.updateSchedule({
        enabled: cronEnabled,
        lead_times_hours: leadTimesHours,
      })
      setMessage({ type: 'success', text: 'Reminder schedule saved.' })
      setTimeout(() => setMessage({ type: '', text: '' }), 2500)
    } catch (e) {
      const code = e?.response?.data?.error
      setScheduleError(
        code === 'migration_missing'
          ? 'Database migration 085 not applied yet.'
          : (e?.response?.data?.message || e.message || 'Could not save reminder schedule.')
      )
    } finally {
      setScheduleSaving(false)
    }
  }

  // Load templates and settings on component mount
  useEffect(() => {
    if (user?.id) {
      loadTemplatesAndSettings();
    }
  }, [user?.id]);

  const loadTemplatesAndSettings = async () => {
    try {
      setLoading(true);
      
      // Load email template
      const emailTemplates = await notificationTemplatesAPI.getTemplates(
        user.id, 
        'email', 
        'appointment_reminder'
      );
      
      if (emailTemplates.length > 0) {
        const template = emailTemplates[0];
        setEmailTemplate({
          subject: template.subject || '',
          content: template.content || ''
        });
        setEnableEmail(template.is_enabled === 1);
      }

      // Load SMS template
      const smsTemplates = await notificationTemplatesAPI.getTemplates(
        user.id, 
        'sms', 
        'appointment_reminder'
      );
      
      if (smsTemplates.length > 0) {
        const template = smsTemplates[0];
        setSmsTemplate({
          content: template.content || ''
        });
        setEnableSMS(template.is_enabled === 1);
      }

      // Load notification settings - handle gracefully if API fails
      try {
        const settings = await notificationSettingsAPI.getSettings(user.id);
        if (settings && Array.isArray(settings) && settings.length > 0) {
          const reminderSetting = settings.find(s => s.notification_type === 'appointment_reminder');
          
          if (reminderSetting) {
            setEnableEmail(reminderSetting.email_enabled === 1);
            setEnableSMS(reminderSetting.sms_enabled === 1);
          }
        }
      } catch (settingsError) {
        // If notification settings API fails, log but don't show error to user
        // The templates are more important, so we'll continue with template-based settings
        console.warn('Could not load notification settings (using template defaults):', settingsError);
        // Don't set error message - templates loaded successfully, settings are optional
      }

    } catch (error) {
      console.error('Error loading templates:', error);
      // Only show error if templates failed to load
      if (error.response?.status !== 500 || !error.config?.url?.includes('notification-settings')) {
        setMessage({ type: 'error', text: 'Failed to load notification templates' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Save email template
      await notificationTemplatesAPI.updateTemplate({
        userId: user.id,
        templateType: 'email',
        notificationName: 'appointment_reminder',
        subject: emailTemplate.subject,
        content: emailTemplate.content,
        isEnabled: enableEmail
      });

      // Save SMS template
      await notificationTemplatesAPI.updateTemplate({
        userId: user.id,
        templateType: 'sms',
        notificationName: 'appointment_reminder',
        subject: null,
        content: smsTemplate.content,
        isEnabled: enableSMS
      });

      // Save notification settings
      await notificationSettingsAPI.updateSetting({
        userId: user.id,
        notificationType: 'appointment_reminder',
        emailEnabled: enableEmail,
        smsEnabled: enableSMS,
        pushEnabled: false
      });

      setMessage({ type: 'success', text: 'Notification settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving templates:', error);
      setMessage({ type: 'error', text: 'Failed to save notification settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsRailLayout title="Appointment reminder" section="Communications" subtitle="Reminder before a scheduled job">

        {/* Content — single stacked column at a readable width. The old
            2-col settings|preview layout cramped both sides; the preview
            now lives in a collapsible card at the bottom. The outer
            wrapper is space-y-6 so settings cards + preview card share
            the same gap. */}
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="space-y-6">
                {/* Notification Type Tabs */}
                <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
                  <div className="flex space-x-1 bg-[var(--sf-bg-page)] rounded-lg p-1 mb-6">
                    <button
                      onClick={() => setActiveTab("email")}
                      className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        activeTab === "email"
                          ? "bg-white text-[var(--sf-blue-500)] shadow-sm"
                          : "text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)]"
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      <span>Email</span>
                    </button>
                    <button
                      onClick={() => setActiveTab("sms")}
                      className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        activeTab === "sms"
                          ? "bg-white text-green-600 shadow-sm"
                          : "text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)]"
                      }`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>SMS</span>
                    </button>
                  </div>

                  {/* Email Settings */}
                  {activeTab === "email" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-[var(--sf-text-primary)]">Enable Appointment Reminder Email</h3>
                          <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                            Sent automatically to the customer to remind them of their upcoming appointment.
                          </p>
                        </div>
                        <SfSwitch
                          checked={enableEmail}
                          onChange={setEnableEmail}
                          showLabel
                          ariaLabel="Enable Appointment Reminder Email"
                        />
                      </div>
                      
                      {/* Test Button for Email */}
                      <NotificationTestButton 
                        notificationType="Appointment Reminder"
                        messageType="email"
                        templateContent="<h2>Appointment Reminder</h2><p>Hi John,</p><p>This is a friendly reminder about your upcoming appointment.</p><p><strong>Service:</strong> Home Cleaning</p><p><strong>Date:</strong> March 15, 2025</p><p><strong>Time:</strong> 10:00 AM - 12:00 PM</p><p><strong>Location:</strong> 123 Main St, Brooklyn, NY</p><p>We look forward to seeing you!</p><p>Best regards,<br />The Team at Just web Agency</p>"
                      />
                    </div>
                  )}

                  {/* SMS Settings */}
                  {activeTab === "sms" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-[var(--sf-text-primary)]">Enable Appointment Reminder SMS</h3>
                          <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                            Sent automatically to the customer to remind them of their upcoming appointment.
                          </p>
                        </div>
                        <SfSwitch
                          checked={enableSMS}
                          onChange={setEnableSMS}
                          showLabel
                          ariaLabel="Enable Appointment Reminder SMS"
                        />
                      </div>
                      
                      {/* Test Button for SMS */}
                      <NotificationTestButton 
                        notificationType="Appointment Reminder"
                        messageType="sms"
                        templateContent="Hi John! Reminder: Your Home Cleaning appointment is tomorrow (March 15) at 10:00 AM. Location: 123 Main St, Brooklyn, NY. We look forward to seeing you! - Just web Agency"
                      />
                    </div>
                  )}
                </div>

                {/* Notification channel — issue #75 phase 2 ─────────── */}
                <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-medium text-[var(--sf-text-primary)] flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-[var(--sf-blue-500)]" />
                        Notification channel
                      </h3>
                      <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                        Which of your connected channels sends automated SMS (reminders, confirmations, status updates). Picks the first connected sendable one automatically if left on <em>Auto</em>.
                      </p>
                    </div>

                    {smsProviderError && (
                      <div className="text-xs text-red-600 flex items-start gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{smsProviderError}</span>
                      </div>
                    )}

                    {smsProviderData ? (
                      <>
                        <select
                          value={smsProviderData.selected || ''}
                          onChange={(e) => handleChangeProvider(e.target.value)}
                          disabled={smsProviderSaving}
                          className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white"
                        >
                          <option value="">Auto (pick first connected)</option>
                          {smsProviderData.providers.map((p) => {
                            const label = PROVIDER_LABELS[p.id] || p.id
                            const suffix = !p.connected
                              ? ' — not connected'
                              : !p.send_implemented
                                ? ' — send coming soon'
                                : ''
                            return (
                              <option key={p.id} value={p.id}>
                                {label}{suffix}
                              </option>
                            )
                          })}
                        </select>

                        <div className="text-xs text-[var(--sf-text-muted)]">
                          {smsProviderData.effective?.ok ? (
                            <span className="text-green-700">
                              Active: sends via <strong>{PROVIDER_LABELS[smsProviderData.effective.provider] || smsProviderData.effective.provider}</strong>
                              {smsProviderData.effective.autoPicked ? ' (auto-picked)' : ''}.
                            </span>
                          ) : smsProviderData.effective?.reason === 'no_provider_connected' ? (
                            <span className="text-amber-700">
                              No SMS provider is connected yet. Visit <a href="/settings/communications/communication-hub" className="text-[var(--sf-blue-500)] hover:underline">Communication hub</a> to connect one.
                            </span>
                          ) : smsProviderData.effective?.reason === 'provider_send_not_implemented_yet' ? (
                            <span className="text-amber-700">
                              <strong>{PROVIDER_LABELS[smsProviderData.effective.provider]}</strong> is connected, but automated sending through it isn't wired up yet. Pick <strong>Twilio</strong> (if connected) in the meantime.
                            </span>
                          ) : smsProviderData.effective?.reason === 'provider_not_connected' ? (
                            <span className="text-amber-700">
                              <strong>{PROVIDER_LABELS[smsProviderData.effective.provider]}</strong> isn't connected on this tenant. Connect it in the Communication hub, or pick a different channel.
                            </span>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-[var(--sf-text-muted)]">Loading…</div>
                    )}
                  </div>
                </div>

                {/* Delivery schedule — issue #76 stage 2 ─────────────── */}
                <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-[var(--sf-text-primary)] flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[var(--sf-blue-500)]" />
                          Delivery schedule
                        </h3>
                        <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                          How far ahead of a scheduled job to send the reminder. Multiple times OK — one SMS per time, per job.
                        </p>
                      </div>
                      <div className="shrink-0">
                        <SfSwitch
                          checked={cronEnabled}
                          onChange={setCronEnabled}
                          disabled={scheduleLoading}
                          showLabel
                          ariaLabel="Automated reminders on/off"
                        />
                      </div>
                    </div>

                    {scheduleLoading ? (
                      <div className="text-sm text-[var(--sf-text-muted)]">Loading schedule…</div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {LEAD_TIME_CHOICES.map((c) => {
                          const checked = leadTimesHours.includes(c.hours)
                          return (
                            <label
                              key={c.hours}
                              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer transition ${
                                checked
                                  ? 'bg-[var(--sf-blue-50,#eff6ff)] border-[var(--sf-blue-500)] text-[var(--sf-text-primary)]'
                                  : 'bg-white border-[var(--sf-border-light)] text-[var(--sf-text-secondary)] hover:border-[var(--sf-text-muted)]'
                              } ${!cronEnabled ? 'opacity-60' : ''}`}
                            >
                              <input
                                type="checkbox"
                                className="rounded border-[var(--sf-border-light)]"
                                checked={checked}
                                onChange={() => toggleLeadTime(c.hours)}
                                disabled={!cronEnabled}
                              />
                              {c.label}
                            </label>
                          )
                        })}
                      </div>
                    )}

                    {scheduleError && (
                      <div className="text-xs text-red-600 flex items-start gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{scheduleError}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-[var(--sf-border-light)]">
                      <p className="text-xs text-[var(--sf-text-muted)]">
                        {cronEnabled
                          ? `Reminders will fire ${leadTimesHours.length} time${leadTimesHours.length === 1 ? '' : 's'} per job.`
                          : 'Automated reminders are off. Turn on to activate the schedule.'}
                      </p>
                      <button
                        onClick={handleSaveSchedule}
                        disabled={scheduleSaving || scheduleLoading}
                        className="px-3 py-1.5 text-sm font-medium rounded-md bg-[var(--sf-blue-500)] text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {scheduleSaving ? 'Saving…' : 'Save schedule'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Recent reminder activity ────────────────────────── */}
                <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-[var(--sf-text-primary)]">Recent reminder activity</h3>
                    <button
                      type="button"
                      onClick={loadDispatches}
                      className="text-xs text-[var(--sf-blue-500)] hover:underline flex items-center gap-1"
                      disabled={dispatchesLoading}
                    >
                      <RefreshCw className={`w-3 h-3 ${dispatchesLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                  {dispatchesLoading ? (
                    <div className="text-sm text-[var(--sf-text-muted)]">Loading…</div>
                  ) : dispatchesError ? (
                    <div className="text-xs text-red-600">{dispatchesError}</div>
                  ) : dispatches.length === 0 ? (
                    <div className="text-sm text-[var(--sf-text-muted)]">
                      No reminders dispatched yet. Once a job's scheduled time crosses one of your lead times above, each attempt (sent, skipped, or failed) will show here.
                    </div>
                  ) : (
                    <div className="divide-y divide-[var(--sf-border-light)] -my-2">
                      {dispatches.map((d) => {
                        const when = d.dispatchedAt ? new Date(d.dispatchedAt).toLocaleString() : ''
                        const StatusIcon = d.status === 'sent' ? CheckCircle2 : d.status === 'failed' ? XCircle : AlertCircle
                        const statusTone = d.status === 'sent'
                          ? 'text-green-600'
                          : d.status === 'failed' ? 'text-red-600' : 'text-amber-600'
                        return (
                          <div key={d.id} className="py-3 flex items-start gap-3">
                            <StatusIcon className={`w-4 h-4 mt-0.5 shrink-0 ${statusTone}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-[var(--sf-text-primary)]">
                                <span className="font-medium">{d.serviceName || `Job #${d.jobId}`}</span>
                                {d.customerName ? <span className="text-[var(--sf-text-muted)]"> · {d.customerName}</span> : null}
                              </div>
                              <div className="text-xs text-[var(--sf-text-muted)] mt-0.5">
                                {d.leadTimeHours}h before · {d.channel.toUpperCase()} · {when}
                              </div>
                              {d.status === 'skipped' && d.skipReason ? (
                                <div className="text-xs text-amber-700 mt-0.5">
                                  skipped — {d.skipReason.replace(/_/g, ' ')}
                                </div>
                              ) : null}
                              {d.status === 'failed' && d.errorMessage ? (
                                <div className="text-xs text-red-700 mt-0.5">
                                  failed — {d.errorMessage}
                                </div>
                              ) : null}
                              {d.status === 'sent' && d.providerMessageId ? (
                                <div className="text-xs text-green-700 mt-0.5 font-mono">
                                  sent · {d.providerMessageId}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Logo Settings (Email only) */}
                {activeTab === "email" && (
                  <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-[var(--sf-text-primary)]">Show logo</h3>
                          <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                            You can add or change your logo in <span className="text-[var(--sf-blue-500)]">Settings > Branding</span>
                          </p>
                        </div>
                        <SfSwitch
                          checked={showLogo}
                          onChange={setShowLogo}
                          ariaLabel="Show logo in email"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Template Editor */}
                <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-[var(--sf-text-primary)]">
                        {activeTab === "email" ? "Email template" : "SMS template"}
                      </h3>
                      <button className="text-[var(--sf-blue-500)] hover:text-[var(--sf-blue-500)] text-sm font-medium">
                        Edit {activeTab}
                      </button>
                    </div>
                    <p className="text-sm text-[var(--sf-text-secondary)]">Customize the content of this {activeTab}</p>
                  </div>
                </div>
              </div>

              {/* Preview Panel — collapsible so it doesn't dominate the
                  layout. Only renders when expanded. */}
              <div className="bg-white rounded-lg border border-[var(--sf-border-light)]">
                <button
                  type="button"
                  onClick={() => setShowPreview((v) => !v)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                  aria-expanded={showPreview}
                >
                  <div>
                    <h3 className="font-medium text-[var(--sf-text-primary)]">
                      {activeTab === "email" ? "Email preview" : "SMS preview"}
                    </h3>
                    <p className="text-sm text-[var(--sf-text-secondary)] mt-0.5">
                      {showPreview ? 'Click to collapse.' : 'See how this message will look to customers.'}
                    </p>
                  </div>
                  <span className="text-[var(--sf-blue-500)] text-sm font-medium">
                    {showPreview ? 'Hide preview' : 'Show preview'}
                  </span>
                </button>
                {showPreview && (
                <div className="px-6 pb-6 space-y-4 border-t border-[var(--sf-border-light)] pt-4">
                  {activeTab === "email" ? (
                    <>
                      {/* Email Header */}
                      <div className="border-b border-[var(--sf-border-light)] pb-4">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-[var(--sf-text-secondary)]">To:</span>
                            <span className="text-[var(--sf-text-primary)]">johnsmith@example.com</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-[var(--sf-text-secondary)]">From:</span>
                            <span className="text-[var(--sf-text-primary)]">Just web Agency</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-[var(--sf-text-secondary)]">Subject:</span>
                            <span className="text-[var(--sf-text-primary)]">Reminder: Appointment Tomorrow</span>
                          </div>
                        </div>
                      </div>

                      {/* Email Content */}
                      <div className="bg-[var(--sf-bg-page)] rounded-lg p-6 min-h-96 overflow-auto">
                        <div className="bg-white rounded-lg p-8 shadow-sm">
                          <div className="space-y-6">
                            <div className="flex items-center space-x-2">
                              <h2 className="text-xl font-semibold text-[var(--sf-text-primary)]">
                                Reminder: Your Appointment is Tomorrow!
                              </h2>
                              <span className="text-blue-500">⏰</span>
                            </div>
                            
                            <div className="space-y-4">
                              <p className="text-[var(--sf-text-primary)]">Hi John Doe,</p>
                              
                              <p className="text-[var(--sf-text-primary)]">
                                This is a friendly reminder that you have an appointment scheduled with us tomorrow. We're looking forward to serving you!
                              </p>
                              
                              <div className="bg-[var(--sf-blue-50)] border border-blue-200 rounded-lg p-4">
                                <h3 className="font-medium text-[var(--sf-text-primary)] mb-3">Appointment Details:</h3>
                                <div className="text-sm text-[var(--sf-text-primary)] space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-[var(--sf-text-secondary)]">Service:</span>
                                    <span className="text-[var(--sf-text-primary)]">Standard Home Cleaning</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-[var(--sf-text-secondary)]">Date & Time:</span>
                                    <span className="text-[var(--sf-text-primary)] font-medium">March 15, 2025 at 10:00 AM</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-[var(--sf-text-secondary)]">Duration:</span>
                                    <span className="text-[var(--sf-text-primary)]">2 hours</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-[var(--sf-text-secondary)]">Location:</span>
                                    <span className="text-[var(--sf-text-primary)]">123 Main St, Brooklyn, NY</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-[var(--sf-text-secondary)]">Team Member:</span>
                                    <span className="text-[var(--sf-text-primary)]">Sarah Johnson</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <h3 className="font-medium text-[var(--sf-text-primary)] mb-2">Preparation Checklist:</h3>
                                <ul className="text-sm text-[var(--sf-text-primary)] space-y-1">
                                  <li>• Clear all surfaces that need to be cleaned</li>
                                  <li>• Secure or remove fragile items</li>
                                  <li>• Ensure pets are safely contained</li>
                                  <li>• Provide access to water and electrical outlets</li>
                                </ul>
                              </div>
                              
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-sm text-green-800 font-medium mb-2">✓ Confirmed and Ready</p>
                                <p className="text-sm text-[var(--sf-text-primary)]">
                                  Your appointment is confirmed. Our team will arrive promptly within the scheduled time window.
                                </p>
                              </div>
                              
                              <p className="text-[var(--sf-text-primary)]">
                                Need to make any changes? Please contact us at least 2 hours before your appointment time.
                              </p>
                              
                              <div className="flex space-x-4">
                                <button className="bg-[var(--sf-blue-500)] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[var(--sf-blue-600)]">
                                  Reschedule
                                </button>
                                <button className="bg-gray-200 text-[var(--sf-text-primary)] px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300">
                                  Cancel
                                </button>
                              </div>
                              
                              <p className="text-[var(--sf-text-primary)]">We can't wait to serve you!</p>
                              <p className="text-[var(--sf-text-primary)]">The Team at Just web Agency</p>
                            </div>
                          </div>
                          
                          <div className="mt-12 pt-6 border-t border-[var(--sf-border-light)] text-center">
                            <p className="text-xs text-[var(--sf-text-muted)]">© 2025 Just web Agency</p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* SMS Preview */}
                      <div className="border-b border-[var(--sf-border-light)] pb-4">
                        <h3 className="font-medium text-[var(--sf-text-primary)]">SMS Preview</h3>
                        <p className="text-sm text-[var(--sf-text-secondary)]">Preview how the SMS will appear to customers</p>
                      </div>

                      <div className="bg-[var(--sf-bg-page)] rounded-lg p-6 min-h-96">
                        <div className="max-w-sm mx-auto">
                          {/* Phone mockup */}
                          <div className="bg-white rounded-2xl shadow-lg p-4 border-8 border-gray-800">
                            <div className="space-y-3">
                              <div className="flex justify-between items-center text-xs text-[var(--sf-text-secondary)]">
                                <span>9:41 AM</span>
                                <span>📶 📶 📶 🔋</span>
                              </div>
                              
                              <div className="space-y-3">
                                <div className="bg-blue-100 rounded-2xl p-3 max-w-xs">
                                  <p className="text-sm text-[var(--sf-text-primary)]">
                                    ⏰ Hi John! Reminder: Your Standard Home Cleaning is tomorrow (March 15) at 10:00 AM. Sarah will be your team member. Please prepare areas to be cleaned. Questions? Reply here. - Just web Agency
                                  </p>
                                  <p className="text-xs text-[var(--sf-text-muted)] mt-1">Just web Agency • now</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className="bg-[var(--sf-blue-500)] text-white px-6 py-2 rounded-md hover:bg-[var(--sf-blue-600)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              </div>
            </SettingsRailLayout>
  )
}

export default AppointmentReminder 